import React, { useState, useEffect, useMemo, useCallback } from "react";
import TaxRateField from "../shared/TaxRateField";
import { toast } from "react-toastify";
import {
  useTaxData,
  useAddCounty,
  useUpdateCountyTax,
  useAddZipCode,
  useUpdateZipCode,
  useDeleteZipCode,
} from "../../hooks/useTaxQueries";
import CountySelector from "./subcomponents/CountySelector";
import ZipCodeManager from "./subcomponents/ZipCodeManager";

const CountyManagementSection = ({
  taxState,
  onDownloadTemplate,
  onFileUpload,
  isLoading,
  styles,
}) => {
  const [selectedCountyName, setSelectedCountyName] = useState("");
  const [zipCodeKey, setZipCodeKey] = useState("");

  const { data: taxData } = useTaxData(taxState);
  const { data: selectedCountyData } = useTaxData(taxState, selectedCountyName);

  const addCountyMutation = useAddCounty();
  const updateCountyTaxMutation = useUpdateCountyTax();
  const addZipCodeMutation = useAddZipCode();
  const updateZipCodeMutation = useUpdateZipCode();
  const deleteZipCodeMutation = useDeleteZipCode();

  const stateAndCounties = useMemo(() => taxData?.counties || [], [taxData]);

  const selectedCounty = useMemo(() => {
    return (
      selectedCountyData?.counties?.find(
        (c) => c.county === selectedCountyName,
      ) || null
    );
  }, [selectedCountyData, selectedCountyName]);

  const zipCodes = useMemo(
    () => selectedCounty?.zipCodes || [],
    [selectedCounty],
  );

  useEffect(() => {
    if (taxState && stateAndCounties.length && !selectedCountyName) {
      setSelectedCountyName(stateAndCounties[0].county || "");
    }
  }, [taxState, stateAndCounties, selectedCountyName]);

  const countyTaxRateValue = useMemo(
    () => Number(selectedCounty?.countyTax || 0),
    [selectedCounty],
  );

  const totalTaxValue = useMemo(() => {
    if (!taxData) return 0;

    let total = 0;

    if (taxData.applyStateTaxToOneTimeFee)
      total += Number(taxData.stateTaxRate || 0);

    if (taxData.applyUseStateTaxToOneTimeFee)
      total += Number(taxData.stateUseTax || 0);

    if (taxData.applyOtherStateTaxToOneTimeFee)
      total += Number(taxData.stateOtherTax || 0);

    if (taxData.applyCountyTaxToOneTimeFee) total += countyTaxRateValue;

    return total;
  }, [taxData, countyTaxRateValue]);

  const handleCountyChange = useCallback((name) => {
    setSelectedCountyName(name);
    setZipCodeKey("");
  }, []);

  const handleAddCounty = useCallback(
    (body) => {
      if (!taxState) return toast.error("Select a state first");
      addCountyMutation.mutate({
        ...body,
        stateId: taxState,
      });
    },
    [taxState, addCountyMutation],
  );

  const handleUpdateCounty = useCallback(
    (body) => {
      const stateCounty = stateAndCounties.find(
        (state) => state.county === selectedCountyName,
      );

      if (!taxState || !stateCounty) return;

      updateCountyTaxMutation.mutate({
        ...body,
        id: stateCounty._id,
        countyId: stateCounty.countyId || stateCounty.county,
      });
    },
    [taxState, selectedCountyName, stateAndCounties, updateCountyTaxMutation],
  );

  const handleAddZipCode = useCallback(
    (zipData, onSuccess) => {
      if (!taxData?._id || !selectedCountyName)
        return toast.error("State or County missing.");

      addZipCodeMutation.mutate(
        {
          stateId: taxData._id,
          countyId: selectedCountyName,
          zipData,
        },
        { onSuccess },
      );
    },
    [taxData, selectedCountyName, addZipCodeMutation],
  );

  const handleUpdateZipCode = useCallback(
    ({ zipCode, newZipCode, percentage }, onSuccess) => {
      if (!taxData?._id || !selectedCountyName)
        return toast.error("State or County missing.");

      updateZipCodeMutation.mutate(
        {
          stateId: taxData._id,
          countyId: selectedCountyName,
          zipCode,
          newZipCode,
        },
        { onSuccess },
      );
    },
    [taxData, selectedCountyName, updateZipCodeMutation],
  );

  const handleDeleteZipCode = useCallback(
    (zipCode, onSuccess) => {
      if (!taxData?._id || !selectedCountyName)
        return toast.error("State or County missing.");

      deleteZipCodeMutation.mutate(
        {
          stateId: taxData._id,
          countyId: selectedCountyName,
          zipCode,
        },
        { onSuccess },
      );
    },
    [taxData, selectedCountyName, deleteZipCodeMutation],
  );

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>County Management</h3>

      <div className={styles.twoColumnGrid}>
        <CountySelector
          stateAndCounties={stateAndCounties}
          selectedCountyName={selectedCountyName}
          onCountyChange={handleCountyChange}
          onSaveCounty={handleUpdateCounty}
          onDeleteCounty={handleUpdateCounty}
          onAddCounty={handleAddCounty}
          isLoading={isLoading}
          taxState={taxState}
          styles={styles}
        />

        <ZipCodeManager
          zipCodes={zipCodes}
          zipCodeKey={zipCodeKey}
          setZipCodeKey={setZipCodeKey}
          selectedCountyName={selectedCountyName}
          taxState={taxState}
          onAddZipCode={handleAddZipCode}
          onUpdateZipCode={handleUpdateZipCode}
          onDeleteZipCode={handleDeleteZipCode}
          onDownloadTemplate={onDownloadTemplate}
          onFileUpload={onFileUpload}
          isLoading={isLoading}
          styles={styles}
        />
      </div>

      <div className={`${styles.twoColumnGrid} ${styles.sectionSpaced}`}>
        <TaxRateField
          label="County Tax Rate (%)"
          value={countyTaxRateValue}
          onSave={(value) =>
            handleUpdateCounty({
              countyTaxRate: value,
            })
          }
          isLoading={isLoading}
          disabled={!selectedCountyName}
        />

        <div className={styles.formGroup}>
          <label className={styles.label}>Total Combined Tax Rate</label>
          <div className={styles.totalTaxDisplay}>
            {totalTaxValue.toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountyManagementSection;
